import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import type {
  Lokalizacja, ObszarSpisu, PozycjaSpisu, RoznicaSpisu, Spis,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Ladowanie, Plakietka, Pole, PoleTekstowe, Przelacznik,
  Przycisk, Pusto,
} from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { STATUS_OBSZARU, STATUS_SPISU } from '../lib/slowniki-serwis';
import { dataGodzina, ilosc } from '../lib/format';

export function Inwentaryzacja() {
  const { maUprawnienie } = useAuth();
  const mozeOtwierac = maUprawnienie('inwentaryzacja.zamkniecie');
  const [otwartyId, setOtwartyId] = useState<number | null>(null);
  const [formularz, setFormularz] = useState(false);

  const lista = useQuery({
    queryKey: ['inwentaryzacja'],
    queryFn: () => api.get<{ pozycje: Spis[] }>('/inwentaryzacja'),
    refetchInterval: 20_000,
  });

  const trwajacy = lista.data?.pozycje.find((s) => s.status !== 'ZAMKNIETA' && s.status !== 'ANULOWANA');

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Inwentaryzacja</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Każde pomieszczenie spisuje się osobno, więc kilka osób pracuje równolegle.
            Stan systemowy utrwala się w chwili zajęcia sali.
          </p>
        </div>
        <PrzyciskExcel dzial="inwentaryzacja" className="ml-auto" />
        {mozeOtwierac && !trwajacy && (
          <Przycisk wariant="glowny" onClick={() => setFormularz(true)}>Otwórz spis</Przycisk>
        )}
      </div>

      {formularz && <FormularzSpisu zamknij={() => setFormularz(false)} />}

      {lista.isLoading ? <Ladowanie /> : (
        lista.data && lista.data.pozycje.length > 0 ? (
          <div className="flex flex-col gap-4">
            {lista.data.pozycje.map((s) => (
              <Karta
                key={s.id}
                tytul={`${s.number} — ${s.name}`}
                opis={`${s.areas.length} ${s.areas.length === 1 ? 'obszar' : 'obszarów'} · ${s._count.lines} pozycji · rozpoczęto ${dataGodzina(s.startedAt)}`}
                akcje={
                  <div className="flex items-center gap-2">
                    <Plakietka ton={STATUS_SPISU[s.status].ton}>{STATUS_SPISU[s.status].etykieta}</Plakietka>
                    <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs"
                      onClick={() => setOtwartyId(otwartyId === s.id ? null : s.id)}>
                      {otwartyId === s.id ? 'Zwiń' : 'Otwórz'}
                    </Przycisk>
                  </div>
                }
              >
                {otwartyId === s.id && <SzczegolySpisu spis={s} mozeZamykac={mozeOtwierac} />}
              </Karta>
            ))}
          </div>
        ) : (
          <Karta>
            <Pusto tytul="Nie było jeszcze żadnego spisu"
              opis="Otwarcie inwentaryzacji tworzy osobny obszar dla każdej wskazanej lokalizacji." />
          </Karta>
        )
      )}
    </div>
  );
}

function SzczegolySpisu({ spis, mozeZamykac }: { spis: Spis; mozeZamykac: boolean }) {
  const qc = useQueryClient();
  const { uzytkownik } = useAuth();
  const [obszarId, setObszarId] = useState<number | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [powodZamkniecia, setPowod] = useState('');
  const [pokazZamkniecie, setPokazZamkniecie] = useState(false);

  const zajmij = useMutation({
    mutationFn: (id: number) => api.post(`/inwentaryzacja/obszary/${id}/zajmij`),
    onSuccess: (_d, id) => { setBlad(null); setObszarId(id); void qc.invalidateQueries({ queryKey: ['inwentaryzacja'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zająć obszaru.'),
  });

  const zwolnij = useMutation({
    mutationFn: (id: number) => api.post(`/inwentaryzacja/obszary/${id}/zwolnij`),
    onSuccess: () => { setBlad(null); setObszarId(null); void qc.invalidateQueries({ queryKey: ['inwentaryzacja'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zwolnić obszaru.'),
  });

  const zamknijSpis = useMutation({
    mutationFn: () => api.post<{ roznic: number; dokumentKorekty: { number: string } | null }>(
      `/inwentaryzacja/${spis.id}/zamknij`, { powod: powodZamkniecia }),
    onSuccess: () => { setPokazZamkniecie(false); setBlad(null); void qc.invalidateQueries(); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zamknąć spisu.'),
  });

  const roznice = useQuery({
    queryKey: ['inwentaryzacja', spis.id, 'roznice'],
    queryFn: () => api.get<{ pozycje: RoznicaSpisu[]; podsumowanie: Record<string, number> }>(
      `/inwentaryzacja/${spis.id}/roznice`),
    refetchInterval: 30_000,
  });

  const zamkniety = spis.status === 'ZAMKNIETA' || spis.status === 'ANULOWANA';

  return (
    <div className="flex flex-col gap-4 border-t border-kamien-200 bg-kamien-50 px-5 py-4">
      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      <div>
        <p className="mb-2 text-xs uppercase tracking-wide text-kamien-500">Obszary spisu</p>
        <ul className="divide-y divide-kamien-200 rounded border border-kamien-200 bg-white">
          {spis.areas.map((o) => (
            <ObszarWiersz
              key={o.id} obszar={o} zamkniety={zamkniety}
              mojeId={uzytkownik?.id ?? -1}
              otwarty={obszarId === o.id}
              onZajmij={() => zajmij.mutate(o.id)}
              onZwolnij={() => zwolnij.mutate(o.id)}
              onOtworz={() => setObszarId(obszarId === o.id ? null : o.id)}
            />
          ))}
        </ul>
      </div>

      {obszarId && <PozycjeObszaru areaId={obszarId} zamkniety={zamkniety} />}

      {roznice.data && roznice.data.pozycje.length > 0 && (
        <Karta tytul="Różnice"
          opis={`${roznice.data.podsumowanie.zRoznica} z ${roznice.data.podsumowanie.pozycji} pozycji · nadwyżek ${roznice.data.podsumowanie.nadwyzek}, niedoborów ${roznice.data.podsumowanie.niedoborow}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Pozycja</th>
                  <th className="px-4 py-2 font-medium">Lokalizacja</th>
                  <th className="px-4 py-2 text-right font-medium">Systemowo</th>
                  <th className="px-4 py-2 text-right font-medium">Spisano</th>
                  <th className="px-4 py-2 text-right font-medium">Różnica</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kamien-200">
                {roznice.data.pozycje.map((r) => (
                  <tr key={r.lineId}>
                    <td className="px-4 py-2">
                      <span className="font-mono text-xs text-kamien-500">{r.nrCsm ?? r.kod}</span>{' '}
                      <span className="text-kamien-900">{r.nazwa}</span>
                      {r.partia && <span className="block text-xs text-kamien-400">partia {r.partia}</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-kamien-600">{r.lokalizacja}</td>
                    <td className="px-4 py-2 text-right text-kamien-600">{ilosc(r.systemowo)}</td>
                    <td className="px-4 py-2 text-right text-kamien-900">{ilosc(r.spisano)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${
                      Number(r.roznica) < 0 ? 'text-status-brak' : 'text-status-nadmiar'}`}>
                      {Number(r.roznica) > 0 ? '+' : ''}{ilosc(r.roznica)} {r.jednostka}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karta>
      )}

      {mozeZamykac && !zamkniety && (
        <div>
          {pokazZamkniecie ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-72 flex-1">
                <Pole etykieta="Uzasadnienie zamknięcia" value={powodZamkniecia}
                  onChange={(e) => setPowod(e.target.value)}
                  podpowiedz="Trafi na dokument korekty różnic i do dziennika" autoFocus />
              </div>
              <Przycisk wariant="ostrzegawczy"
                disabled={zamknijSpis.isPending || powodZamkniecia.trim().length < 3}
                onClick={() => zamknijSpis.mutate()}>
                {zamknijSpis.isPending ? 'Zamykanie...' : 'Zamknij spis i zaksięguj różnice'}
              </Przycisk>
              <Przycisk wariant="cichy" onClick={() => setPokazZamkniecie(false)}>Anuluj</Przycisk>
            </div>
          ) : (
            <Przycisk wariant="ostrzegawczy" onClick={() => setPokazZamkniecie(true)}>
              Zamknij inwentaryzację
            </Przycisk>
          )}
          <p className="mt-2 text-xs text-kamien-500">
            Zamknięcie zamienia różnice materiałowe w dokument korekty. Braki sprzętu trafiają
            do protokołu, ale księgi nie ruszają — to sprawa do wyjaśnienia, nie automat.
          </p>
        </div>
      )}
    </div>
  );
}

function ObszarWiersz({ obszar, zamkniety, mojeId, otwarty, onZajmij, onZwolnij, onOtworz }: {
  obszar: ObszarSpisu; zamkniety: boolean; mojeId: number; otwarty: boolean;
  onZajmij: () => void; onZwolnij: () => void; onOtworz: () => void;
}) {
  const mojaBlokada = obszar.lockedBy?.id === mojeId;
  const cudzaBlokada = obszar.lockedBy && !mojaBlokada;

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
      <span className="font-mono text-xs text-kamien-500">{obszar.location.code}</span>
      <span className="flex-1 text-kamien-900">{obszar.location.name}</span>
      <span className="text-xs text-kamien-400">{obszar._count.lines} poz.</span>
      <Plakietka ton={STATUS_OBSZARU[obszar.status].ton}>
        {STATUS_OBSZARU[obszar.status].etykieta}
      </Plakietka>
      {cudzaBlokada && (
        <span className="text-xs text-status-uzupelnij">
          spisuje {obszar.lockedBy!.fullName}
        </span>
      )}
      {!zamkniety && (
        <div className="flex gap-1">
          {mojaBlokada ? (
            <>
              <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs" onClick={onOtworz}>
                {otwarty ? 'Zwiń pozycje' : 'Spisuj'}
              </Przycisk>
              <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs" onClick={onZwolnij}>
                Zwolnij
              </Przycisk>
            </>
          ) : obszar.status === 'SPISANA' || obszar.status === 'ZATWIERDZONA' ? (
            <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs" onClick={onOtworz}>
              Podejrzyj
            </Przycisk>
          ) : (
            <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs" onClick={onZajmij}>
              Zajmij salę
            </Przycisk>
          )}
        </div>
      )}
    </li>
  );
}

function PozycjeObszaru({ areaId, zamkniety }: { areaId: number; zamkniety: boolean }) {
  const qc = useQueryClient();
  const [blad, setBlad] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['inwentaryzacja', 'obszar', areaId],
    queryFn: () => api.get<{ pozycje: PozycjaSpisu[] }>(`/inwentaryzacja/obszary/${areaId}/pozycje`),
  });

  const zapisz = useMutation({
    mutationFn: (p: { id: number; qtyCounted: string; version: number }) =>
      api.put(`/inwentaryzacja/pozycje/${p.id}`, {
        qtyCounted: p.qtyCounted === '' ? null : Number(p.qtyCounted.replace(',', '.')),
        version: p.version,
      }),
    onSuccess: () => { setBlad(null); void qc.invalidateQueries({ queryKey: ['inwentaryzacja'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać ilości.'),
  });

  const zamknijObszar = useMutation({
    mutationFn: () => api.post(`/inwentaryzacja/obszary/${areaId}/zamknij`),
    onSuccess: () => { setBlad(null); void qc.invalidateQueries({ queryKey: ['inwentaryzacja'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zamknąć obszaru.'),
  });

  if (isLoading) return <Ladowanie />;
  const pozycje = data?.pozycje ?? [];
  const niepoliczone = pozycje.filter((p) => p.qtyCounted === null).length;

  return (
    <Karta tytul="Spis z natury"
      opis={`${pozycje.length} pozycji · niepoliczonych ${niepoliczone}`}>
      {blad && <div className="px-5 pt-4"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}

      {pozycje.length === 0 ? (
        <Pusto tytul="W tej lokalizacji nie ma nic na stanie"
          opis="Jeśli coś tu leży, znaczy to, że nie zostało przyjęte dokumentem." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                <th className="px-4 py-2 font-medium">Pozycja</th>
                <th className="px-4 py-2 text-right font-medium">Systemowo</th>
                <th className="px-4 py-2 font-medium">Spisano</th>
                <th className="px-4 py-2 text-right font-medium">Różnica</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-200">
              {pozycje.map((p) => (
                <WierszSpisu key={p.id} pozycja={p} zamkniety={zamkniety}
                  onZapisz={(qty) => zapisz.mutate({ id: p.id, qtyCounted: qty, version: p.version })} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!zamkniety && pozycje.length > 0 && (
        <div className="border-t border-kamien-200 px-5 py-4">
          <Przycisk wariant="glowny" disabled={zamknijObszar.isPending || niepoliczone > 0}
            onClick={() => zamknijObszar.mutate()}>
            {zamknijObszar.isPending ? 'Zamykanie...' : 'Zakończ spis tej sali'}
          </Przycisk>
          {niepoliczone > 0 && (
            <p className="mt-2 text-sm text-status-uzupelnij">
              Zostało {niepoliczone} pozycji bez wpisanej ilości. Wpisz zero tam, gdzie faktycznie
              nic nie ma — puste pole to nie to samo co zero.
            </p>
          )}
        </div>
      )}
    </Karta>
  );
}

function WierszSpisu({ pozycja, zamkniety, onZapisz }: {
  pozycja: PozycjaSpisu; zamkniety: boolean; onZapisz: (qty: string) => void;
}) {
  const [wartosc, setWartosc] = useState(pozycja.qtyCounted ?? '');
  const roznica = pozycja.qtyCounted === null
    ? null
    : Number(pozycja.qtyCounted) - Number(pozycja.qtySystem);

  return (
    <tr className={roznica !== null && roznica !== 0 ? 'bg-amber-50/40' : ''}>
      <td className="px-4 py-2">
        <span className="font-mono text-xs text-kamien-500">
          {pozycja.equipmentUnit?.nrCsm ?? pozycja.item.code}
        </span>{' '}
        <span className="text-kamien-900">{pozycja.item.name}</span>
        {pozycja.item.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
        {pozycja.batch && (
          <span className="block text-xs text-kamien-400">partia {pozycja.batch.lotNumber ?? pozycja.batch.id}</span>
        )}
      </td>
      <td className="px-4 py-2 text-right text-kamien-600">
        {ilosc(pozycja.qtySystem)} {pozycja.item.unit}
      </td>
      <td className="px-4 py-2">
        <input
          inputMode="decimal"
          disabled={zamkniety}
          value={wartosc}
          onChange={(e) => setWartosc(e.target.value)}
          onBlur={() => { if (wartosc !== (pozycja.qtyCounted ?? '')) onZapisz(wartosc); }}
          aria-label={`Ilość spisana — ${pozycja.item.name}`}
          className="min-h-10 w-28 rounded-md border border-kamien-300 bg-white px-2 text-right text-sm
            disabled:bg-kamien-100 disabled:text-kamien-400"
        />
      </td>
      <td className={`px-4 py-2 text-right font-medium ${
        roznica === null ? 'text-kamien-400'
          : roznica === 0 ? 'text-status-ok'
            : roznica < 0 ? 'text-status-brak' : 'text-status-nadmiar'}`}>
        {roznica === null ? '—' : roznica === 0 ? 'zgadza się' : `${roznica > 0 ? '+' : ''}${roznica}`}
      </td>
    </tr>
  );
}

function FormularzSpisu({ zamknij }: { zamknij: () => void }) {
  const qc = useQueryClient();
  const [dane, setDane] = useState({
    name: `Spis ${new Date().toLocaleDateString('pl-PL')}`,
    includesMaterials: true, includesEquipment: true, notes: '',
  });
  const [zaznaczone, setZaznaczone] = useState<Set<number>>(new Set());
  const [blad, setBlad] = useState<string | null>(null);

  const lokalizacje = useQuery({
    queryKey: ['lokalizacje', 'do-spisu'],
    queryFn: () => api.get<{ pozycje: Lokalizacja[] }>('/lokalizacje?tylkoAktywne=true'),
  });

  const otworz = useMutation({
    mutationFn: () => api.post('/inwentaryzacja', { ...dane, locationIds: [...zaznaczone] }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inwentaryzacja'] }); zamknij(); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się otworzyć spisu.'),
  });

  return (
    <Karta tytul="Nowa inwentaryzacja">
      <form className="flex flex-col gap-4 px-5 py-5"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); otworz.mutate(); }}>
        {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Pole etykieta="Nazwa spisu" value={dane.name}
            onChange={(e) => setDane({ ...dane, name: e.target.value })} required />
          <div className="flex flex-col justify-end gap-1">
            <Przelacznik etykieta="Obejmuje materiały" checked={dane.includesMaterials}
              onChange={(e) => setDane({ ...dane, includesMaterials: e.target.checked })} />
            <Przelacznik etykieta="Obejmuje sprzęt" checked={dane.includesEquipment}
              onChange={(e) => setDane({ ...dane, includesEquipment: e.target.checked })} />
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-kamien-700">
            Lokalizacje do spisania ({zaznaczone.size} wybranych)
          </p>
          <div className="max-h-64 overflow-y-auto rounded border border-kamien-200">
            {lokalizacje.data?.pozycje.map((l) => (
              <label key={l.id}
                className="flex cursor-pointer items-center gap-3 border-b border-kamien-100 px-4 py-2 text-sm last:border-0 hover:bg-kamien-50">
                <input type="checkbox" className="size-4 accent-bordo-600"
                  checked={zaznaczone.has(l.id)}
                  onChange={(e) => setZaznaczone((s) => {
                    const n = new Set(s);
                    if (e.target.checked) n.add(l.id); else n.delete(l.id);
                    return n;
                  })} />
                <span className="font-mono text-xs text-kamien-500">{l.code}</span>
                <span className="flex-1 text-kamien-800">{l.path ?? l.name}</span>
                {!l.isStorage && <Plakietka>bez składowania</Plakietka>}
              </label>
            ))}
          </div>
        </div>

        <PoleTekstowe etykieta="Uwagi" value={dane.notes}
          onChange={(e) => setDane({ ...dane, notes: e.target.value })} />

        <div className="flex gap-3">
          <Przycisk type="submit" wariant="glowny"
            disabled={otworz.isPending || zaznaczone.size === 0}>
            {otworz.isPending ? 'Otwieranie...' : `Otwórz spis (${zaznaczone.size} lokalizacji)`}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
